import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Button, Link, Stack, Typography } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import { verifyEmail } from "../api/auth";

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const initialEmail = searchParams.get("email") || "";
  const initialVerificationUrl = searchParams.get("verificationUrl") || "";
  const returnTo = searchParams.get("returnTo") || "/dashboard";
  const isSentView = searchParams.get("sent") === "1" || !token;

  const [status, setStatus] = useState(isSentView ? "sent" : "verifying");
  const [message, setMessage] = useState("");
  const [verificationUrl] = useState(initialVerificationUrl);

  const resendPath = useMemo(() => {
    const params = new URLSearchParams();

    if (initialEmail) {
      params.set("identifier", initialEmail);
    }

    if (returnTo) {
      params.set("returnTo", returnTo);
    }

    const query = params.toString();
    return query ? `/resend-verification?${query}` : "/resend-verification";
  }, [initialEmail, returnTo]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let isMounted = true;

    const runVerification = async () => {
      setStatus("verifying");
      setMessage("");

      try {
        await verifyEmail(token);

        if (isMounted) {
          setStatus("verified");
        }
      } catch (err) {
        if (isMounted) {
          setStatus("expired");
          setMessage(err.response?.data?.error || t("authVerify.expired"));
        }
      }
    };

    runVerification();

    return () => {
      isMounted = false;
    };
  }, [t, token]);

  const showResendAction = status === "sent" || status === "expired";

  return (
    <AuthPageShell
      compact
      title={t("authVerify.title")}
      subtitle={t("authVerify.subtitle")}
    >
      <Stack spacing={2}>
        {status === "verifying" && (
          <Alert severity="info" icon={<MarkEmailReadIcon />}>
            {t("authVerify.verifying")}
          </Alert>
        )}

        {status === "verified" && (
          <Stack spacing={2}>
            <Alert severity="success" icon={<MarkEmailReadIcon />}>
              {t("authVerify.success")}
            </Alert>
            <Typography color="text.secondary">
              {t("authVerify.continueInApp")}
            </Typography>
            <Button
              component={RouterLink}
              to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
              variant="contained"
              startIcon={<LoginIcon />}
              fullWidth
            >
              {t("authVerify.enterTavern")}
            </Button>
          </Stack>
        )}

        {status === "sent" && (
          <Stack spacing={1.25}>
            <Alert severity="info" icon={<MarkEmailReadIcon />}>
              {t("authVerify.sent")}
            </Alert>
            <Button
              component="a"
              href="mailto:"
              variant="outlined"
              startIcon={<MarkEmailReadIcon />}
              fullWidth
            >
              {t("authVerify.openMailbox")}
            </Button>
          </Stack>
        )}

        {status === "expired" && (
          <Alert severity="warning">
            {message || t("authVerify.expired")}
          </Alert>
        )}

        {showResendAction && (
          <Stack spacing={1.5}>
            {status === "expired" && (
              <Typography color="text.secondary">
                {t("authVerify.resendCopy")}
              </Typography>
            )}
            <Button
              component={RouterLink}
              to={resendPath}
              variant="contained"
              startIcon={<ReplayIcon />}
              fullWidth
            >
              {t("authVerify.resend")}
            </Button>
          </Stack>
        )}

        {verificationUrl && (
          <Alert severity="info">
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {t("authVerify.devVerifyLink")}{" "}
              <Link
                component={RouterLink}
                to={
                  (() => {
                    const url = new URL(verificationUrl);
                    url.searchParams.set("returnTo", returnTo);

                    return url.pathname + url.search;
                  })()
                }
              >
                {verificationUrl}
              </Link>
            </Typography>
          </Alert>
        )}

        {status !== "verified" && (
          <Link
            component={RouterLink}
            to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
            underline="hover"
          >
            {t("authVerify.backToLogin")}
          </Link>
        )}
      </Stack>
    </AuthPageShell>
  );
};

export default VerifyEmailPage;
